import { FC, useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ItemType } from "antd/lib/menu/interface";
import {
  PlusOutlined,
  TeamOutlined,
  HomeOutlined,
  ContactsOutlined,
  ReadOutlined,
  FileDoneOutlined,
  AudioMutedOutlined,
  ExpandOutlined,
  ScanOutlined,
} from "@ant-design/icons";

import { useClientSettings } from "@hooks/useClientSettings";
import usePermissions from "@hooks/usePermissions";
import { featureFlags } from "@settings";

import { CorbanSelector } from "./CorbanSelector";
import { Menu, Container, Content } from "./styles";
import { LOCAL_STORAGE } from "@constants";
import { theme } from "antd";

interface Props {
  onItemClick?: () => void;
}

export const SidebarMenuV2: FC<Props> = ({ onItemClick }) => {
  const BENEFIT_CARD_NAME = useClientSettings().benefitCardName;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { routes, homeRoute } = usePermissions();

  const currentRoute = pathname.substring(1);
  const routeSplit = currentRoute.split("/");
  const currentSelectedSub1 = routeSplit[1];
  const currentSelectedSub2 = `${routeSplit[0]}/${routeSplit[1]}`;
  const hasJourney = localStorage.getItem(LOCAL_STORAGE.LAST_JOURNEY);

  const hasUsersCrud = featureFlags.enableUsersCrud;

  const [openKeys, setOpenKeys] = useState<string[]>([
    currentSelectedSub1,
    currentSelectedSub2,
  ]);

  const isAllowedItemType = useCallback(
    (itemType: ItemType) =>
      itemType?.key ? !!routes[`/${itemType.key}`] : true,
    [routes],
  );

  const { token } = theme.useToken();

  const getAllowedItemTypes = useCallback(
    (
      itemTypeList: ((ItemType & { children?: ItemType[] }) | null)[],
    ): ItemType[] => {
      return itemTypeList.reduce((allowedItemTypes: ItemType[], itemType) => {
        if (itemType && isAllowedItemType(itemType)) {
          if (itemType.children) {
            return [
              ...allowedItemTypes,
              {
                ...itemType,
                children: getAllowedItemTypes(itemType.children),
              },
            ];
          }

          return [...allowedItemTypes, itemType];
        }

        return allowedItemTypes;
      }, []);
    },
    [isAllowedItemType],
  );
  const menuItems = useMemo(() => {
    const fullMenu: ItemType[] = [
      { type: "divider" },
      {
        label: "Home de serviços",
        key: "servicos",
        icon: <HomeOutlined style={{ paddingLeft: "4.5px" }} />,
        children: [
          {
            label: "INSS e Servidores Públicos",
            key: "inss",
            icon: "",
            children: [
              {
                label: "Aumento Salarial",
                key: "inss/aumento-margem",
                icon: "",
              },
              ...(featureFlags.enableBatchTypingMarginIncrease
                ? [
                    {
                      label: "Digitação em lote (Aumento de Margem)",
                      key: "inss/aumento-margem-lote",
                      icon: "",
                    },
                  ]
                : []),
              {
                label: "Margem Livre",
                key: "inss/margem-livre",
                icon: "",
              },
              {
                label: "Portabilidade",
                key: "inss/portabilidade",
                icon: "",
              },
              {
                label: "Portabilidade + Refin",
                key: "inss/portabilidade-refin",
                icon: "",
              },
              {
                label: "Refinanciamento",
                key: "inss/refinanciamento",
                icon: "",
              },
            ],
          },
          {
            label: "Cartão Consignado",
            key: "cartao",
            icon: "",
            children: [
              {
                label: BENEFIT_CARD_NAME,
                key: "cartao/cartao-beneficio",
                icon: "",
              },
              {
                label: "Agregação de Margem",
                key: "cartao/agregacao-margem",
                icon: "",
              },
            ],
          },
          (featureFlags.enableProductReceba && {
            label: "Receba",
            key: "receba",
            icon: "",
          }) as ItemType,
          ((featureFlags.enablePrivateConsigLoan ||
            featureFlags.enablePrivateConsigCef) && {
            label: "Privado (CLT)",
            key: "consignado-privado",
            icon: "",
          }) as ItemType,
        ],
      },
      {
        label: "Contratos",
        key: "contratos",
        icon: <ContactsOutlined />,
      },
      {
        label: featureFlags.enableContractsRetentionLayout
          ? "Retenção"
          : "Portabilidade Ataque",
        key: "portabilidade-ataque",
        icon: featureFlags.enableContractsRetentionLayout ? (
          <ExpandOutlined />
        ) : (
          <ScanOutlined />
        ),
      },
    ];

    if (featureFlags.enableTrainingTab) {
      fullMenu.push({
        label: "Treinamentos",
        key: "treinamentos",
        icon: <ReadOutlined />,
      });
    }

    if (
      featureFlags.enableDoNotDisturbTab ||
      featureFlags.enableDoNotDisturbTabCef
    ) {
      fullMenu.push({
        label: "Não Me Perturbe",
        key: "nao-me-perturbe",
        icon: <AudioMutedOutlined />,
      });
    }

    if (hasUsersCrud) {
      fullMenu.push(
        {
          label: "Usuários",
          key: "usuarios",
          icon: <TeamOutlined />,
        },
        {
          label: "Adicionar Usuário",
          key: "usuarios/novo",
          icon: <PlusOutlined />,
        },
      );
    }

    if (featureFlags.enableSupportMaterials) {
      fullMenu.push({
        label: "Materiais de Apoio",
        key: "materiais-de-apoio",
        icon: <FileDoneOutlined />,
      });
    }

    return getAllowedItemTypes(fullMenu);
  }, [getAllowedItemTypes, hasUsersCrud, BENEFIT_CARD_NAME]);

  const handleClick = ({ key }: { key: string }) => {
    const isJourneyEnd = pathname.includes(key) && !hasJourney;
    onItemClick?.();
    if (isJourneyEnd) return void navigate(homeRoute);
    void navigate(`/${key}`);
  };

  return (
    <Container>
      <Content>
        <CorbanSelector />
        <Menu
          mode="inline"
          items={menuItems}
          onClick={handleClick}
          selectedKeys={[currentRoute]}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          $primaryColor={token.colorPrimary}
        />
      </Content>
    </Container>
  );
};
